import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Target, Eye, Download, BookOpen, Users, Heart, Megaphone, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const HERO_BG = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";
const QURAN_IMG = "https://images.pexels.com/photos/8522572/pexels-photo-8522572.jpeg";
const MOSQUE_IMG = "https://images.unsplash.com/photo-1733760746690-f07b1d6015cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtb3NxdWUlMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzcwNTQwMDgxfDA&ixlib=rb-4.1.0&q=85";

// Default/placeholder ads when no ads are available
const PLACEHOLDER_ADS = [
  {
    id: 'placeholder-1',
    business_name: 'Islamic Bookstore Kenya',
    title: '20% Discount for AKIT Members',
    description: 'Quality Islamic books, Qurans, and educational materials.',
    image_url: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400',
    link_url: null,
    ad_type: 'homepage_banner',
  },
  {
    id: 'placeholder-2',
    business_name: 'Halal Foods Kenya',
    title: 'Premium Halal Products',
    description: 'Certified halal food products for schools and institutions.',
    image_url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400',
    link_url: null,
    ad_type: 'sponsored_resource',
  },
  {
    id: 'placeholder-3',
    business_name: 'Islamic Academy Online',
    title: 'Professional Development Courses',
    description: 'Enhance your teaching skills with certified online courses.',
    image_url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400',
    link_url: null,
    ad_type: 'homepage_banner',
  },
];

export const HomePage = () => {
  const { isAuthenticated, api, API } = useAuth();
  const [advertisements, setAdvertisements] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAdvertisements();
    fetchMemberCount();
  }, []);

  const fetchMemberCount = async () => {
    try {
      const count = await api.getMemberCount();
      setMemberCount(count);
    } catch (error) {
      console.error('Error fetching member count:', error);
    }
  };

  const fetchAdvertisements = async () => {
    try {
      const response = await fetch(`${API}/advertisements`);
      if (response.ok) {
        const data = await response.json();
        setAdvertisements(data.length > 0 ? data : PLACEHOLDER_ADS);
      } else {
        setAdvertisements(PLACEHOLDER_ADS);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      setAdvertisements(PLACEHOLDER_ADS);
    }
  };

  const scrollAds = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleAdClick = (ad, e) => {
    e.preventDefault();
    if (ad.link_url && ad.link_url !== '#') {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/advertise?type=${ad.ad_type || 'homepage_banner'}`);
    }
  };

  const features = [
    {
      icon: Download,
      title: 'Exam Resources',
      description: 'Access KCSE revision papers, mock exams, and end-of-term assessments.',
    },
    {
      icon: BookOpen,
      title: 'Teaching Notes',
      description: 'Comprehensive teaching materials covering Seerah, Fiqh, and Islamic History.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Connect with fellow IRE teachers across Kenya for collaboration and support.',
    },
    {
      icon: Heart,
      title: 'Welfare Support',
      description: 'Access welfare programs designed to support teachers in times of need.',
    },
  ];

  const testimonials = [
    {
      name: 'Ustadh Ahmed Juma',
      location: 'Mombasa County',
      quote: 'AKIT has provided me with the resources I needed to better manage my classroom and connect with other professionals.',
    },
    {
      name: 'Mualima Sarah Ali',
      location: 'Nairobi County',
      quote: 'The professional workshops organized by the association are transformative for our career growth.',
    },
  ];

  // Format member count for display
  const formatMemberCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K+`;
    }
    return `${count}+`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 hero-glow" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl py-20 text-center">
          {/* Member Count Badge */}
          {memberCount > 0 && (
            <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/30 text-primary px-4 py-2 rounded-full mb-6 animate-fade-in" data-testid="member-count-badge">
              <Users className="h-4 w-4" />
              <span className="text-sm font-bold">{formatMemberCount(memberCount)} Teachers Strong</span>
              <TrendingUp className="h-4 w-4 text-green-400" />
            </div>
          )}

          <p className="text-primary font-amiri text-2xl md:text-3xl mb-4 animate-fade-in">
            بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم
          </p>
          <p className="text-muted-foreground text-sm mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            In the name of Allah, the Most Gracious, the Most Merciful
          </p>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-foreground mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            ASSOCIATION OF <span className="text-primary">KENYA</span>
            <br />IRE TEACHERS
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Empowering Islamic Religious Education teachers through professional development, 
            resource sharing, and collective advocacy.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="btn-primary text-lg px-8 py-6" data-testid="hero-dashboard-btn">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/register">
                  <Button className="btn-primary text-lg px-8 py-6" data-testid="hero-join-btn">
                    Join AKIT Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/resources">
                  <Button variant="outline" className="btn-secondary text-lg px-8 py-6" data-testid="hero-resources-btn">
                    Browse Resources
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* Member Counter Stats Bar */}
      <section className="py-6 bg-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-heading font-bold text-primary">{formatMemberCount(memberCount)}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Registered Teachers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-heading font-bold text-foreground">47</p>
              <p className="text-xs md:text-sm text-muted-foreground">Counties Covered</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-heading font-bold text-foreground">100%</p>
              <p className="text-xs md:text-sm text-muted-foreground">Free Resources</p>
            </div>
            {!isAuthenticated && (
              <Link to="/register" className="hidden md:block">
                <Button className="btn-primary">
                  Join Our Community
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-black/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card p-8 rounded-2xl hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-primary mb-3">Our Mission</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    To empower IRE teachers across Kenya through professional development, 
                    resource sharing, and collective advocacy for educational excellence in Islamic studies.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-2xl hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Eye className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-primary mb-3">Our Vision</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    A leading professional body fostering excellence in Islamic Religious Education 
                    and nurturing character in the next generation of believers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Article */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div 
                className="h-64 md:h-auto bg-cover bg-center"
                style={{ backgroundImage: `url(${QURAN_IMG})` }}
              />
              <div className="p-8 md:p-12">
                <span className="inline-block bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                  Weekly Article
                </span>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                  The Role of Modern Pedagogy in IRE
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Islamic Religious Education in Kenya is evolving. As teachers, we are tasked not only with 
                  transmitting knowledge but also with adapting to digital tools that engage the modern student. 
                  Integrating ethics with 21st-century skills ensures our students remain competitive while 
                  staying true to their faith.
                </p>
                <Button variant="ghost" className="text-primary hover:text-primary/80 p-0" data-testid="read-more-btn">
                  Read More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-black/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What We Offer
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive resources and support for Islamic Religious Education teachers across Kenya.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="glass-card p-6 rounded-2xl hover:border-primary/50 hover:scale-105 transition-all cursor-pointer group"
                data-testid={`feature-card-${index}`}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/40 transition-colors">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Partners / Advertisements Carousel */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                  Featured Partners
                </h2>
                <span className="text-[10px] text-muted-foreground uppercase tracking-widest bg-muted px-2 py-1 rounded">
                  Halal Sponsors
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Islamically permissible businesses supporting AKIT</p>
            </div>
            <Link to="/advertise" className="hidden md:flex items-center gap-2 text-primary hover:underline text-sm font-medium" data-testid="advertise-link">
              <Megaphone className="h-4 w-4" />
              Advertise with Us
            </Link>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Scroll Buttons */}
            <button 
              onClick={() => scrollAds('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full glass-card flex items-center justify-center text-foreground hover:bg-white/10 transition-colors -translate-x-4 hidden md:flex"
              data-testid="scroll-ads-left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button 
              onClick={() => scrollAds('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full glass-card flex items-center justify-center text-foreground hover:bg-white/10 transition-colors translate-x-4 hidden md:flex"
              data-testid="scroll-ads-right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Scrollable Ads */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto no-scrollbar pb-4 -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth"
              data-testid="ads-carousel"
            >
              {advertisements.map((ad, index) => (
                <div
                  key={ad.id}
                  onClick={(e) => handleAdClick(ad, e)}
                  className="min-w-[300px] md:min-w-[350px] glass-card rounded-2xl overflow-hidden hover:border-primary/50 hover:scale-[1.02] transition-all group flex-shrink-0 cursor-pointer"
                  data-testid={`ad-card-${index}`}
                >
                  {ad.image_url && (
                    <div 
                      className="h-40 bg-cover bg-center relative"
                      style={{ backgroundImage: `url(${ad.image_url})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex gap-2">
                        <span className="bg-primary/90 text-primary-foreground text-[10px] font-bold px-2 py-1 rounded uppercase">
                          Sponsored
                        </span>
                        {(!ad.link_url || ad.link_url === '#') && (
                          <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-1 rounded uppercase">
                            Your Ad Here
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="p-5">
                    <p className="text-lg font-heading font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
                      {ad.business_name}
                    </p>
                    <p className="text-primary text-sm font-medium mb-2">{ad.title}</p>
                    <p className="text-muted-foreground text-sm line-clamp-2">{ad.description}</p>
                    {(!ad.link_url || ad.link_url === '#') && (
                      <p className="text-primary text-xs mt-3 flex items-center gap-1">
                        <Megaphone className="h-3 w-3" />
                        Click to advertise here
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Advertise Link */}
          <div className="mt-6 text-center md:hidden">
            <Link to="/advertise" className="inline-flex items-center gap-2 text-primary text-sm font-medium">
              <Megaphone className="h-4 w-4" />
              Advertise with Us
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Member Testimonials
            </h2>
          </div>

          <div className="flex gap-6 overflow-x-auto pb-4 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-2">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="min-w-[300px] md:min-w-0 glass-card p-6 rounded-2xl"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-foreground">{testimonial.name}</p>
                    <p className="text-xs text-primary">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-muted-foreground italic text-sm">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        className="py-20 relative"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.9)), url(${MOSQUE_IMG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Join the AKIT Community Today
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
            Connect with fellow educators, access premium resources, and contribute to the 
            advancement of Islamic Religious Education in Kenya.
          </p>
          {memberCount > 0 && (
            <p className="text-primary font-semibold mb-8">
              Join {formatMemberCount(memberCount)} teachers already benefiting from AKIT membership!
            </p>
          )}
          {!isAuthenticated && (
            <Link to="/register">
              <Button className="btn-primary text-lg px-8 py-6" data-testid="cta-join-btn">
                Become a Member
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};
